import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import EmptyState, { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, Search, ShieldCheck, ShieldOff } from "lucide-react";
import { PrivacyBadge } from "@/components/common/PrivacySafe";

export default function Members() {
  const t = useT();
  const qc = useQueryClient();
  const [query, setQuery] = useState("");

  const people = useQuery({
    queryKey: ["admin-people"],
    queryFn: async () => (await api.get("/platform/people")).data,
    retry: false,
  });

  const items = useMemo(() => {
    const raw = Array.isArray(people.data) ? people.data : people.data?.people || people.data?.items || [];
    if (!query) return raw;
    const s = query.toLowerCase();
    return raw.filter((u) => (u.name || "").toLowerCase().includes(s) || (u.email || "").toLowerCase().includes(s));
  }, [people.data, query]);

  const action = async (user_id, action_key) => {
    try {
      await api.post(`/platform/people/${user_id}/actions`, { action: action_key });
      toast.success(t("common.saved"));
      qc.invalidateQueries({ queryKey: ["admin-people"] });
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow={t("nav.people")}
        title={t("nav.users")}
        subtitle="Roles, teams and access."
        actions={<PrivacyBadge />}
      />

      <div className="flex items-center gap-2">
        <Search className="h-4 w-4 text-ink-500" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("common.search")}
          className="rounded-xl bg-white h-11 max-w-md"
          data-testid="users-search"
        />
      </div>

      {people.isLoading && <LoadingCard />}
      {people.isError && <ErrorState message={errMsg(people.error)} onRetry={() => people.refetch()} />}
      {!people.isLoading && !people.isError && items.length === 0 && (
        <EmptyState icon={Users} title={t("common.empty")} testId="users-empty" />
      )}

      <div className="rounded-3xl bg-white shadow-card overflow-hidden" data-testid="users-table">
        <table className="min-w-full text-sm">
          <thead className="bg-cream-50">
            <tr className="text-left text-ink-500 text-[11px] uppercase tracking-[0.15em]">
              <th className="py-3 px-4">{t("common.name")}</th>
              <th className="py-3 px-4">{t("common.email")}</th>
              <th className="py-3 px-4">{t("common.role")}</th>
              <th className="py-3 px-4">{t("nav.teams")}</th>
              <th className="py-3 px-4">{t("common.status")}</th>
              <th className="py-3 px-4 text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((u, i) => {
              const id = u.id || u.user_id;
              return (
                <tr key={id || i} className="border-t border-border" data-testid={`users-row-${i}`}>
                  <td className="py-3 px-4 text-ink-900">{u.name || "—"}</td>
                  <td className="py-3 px-4 text-ink-700">{u.email || "—"}</td>
                  <td className="py-3 px-4 text-ink-700">{Array.isArray(u.roles) ? u.roles.join(", ") : (u.role || "employee")}</td>
                  <td className="py-3 px-4 text-ink-700">{u.team_name || u.team || "—"}</td>
                  <td className="py-3 px-4">
                    <span className="inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium bg-cream-100 text-ink-700">
                      {u.status || "active"}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="inline-flex gap-1.5">
                      <Button size="sm" variant="outline" className="rounded-full h-8 border-border" data-testid={`users-suspend-${i}`} onClick={() => action(id, "suspend")}>
                        <ShieldOff className="h-3.5 w-3.5 mr-1" /> Suspend
                      </Button>
                      <Button size="sm" variant="outline" className="rounded-full h-8 border-border" data-testid={`users-restore-${i}`} onClick={() => action(id, "restore")}>
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {t("common.restore")}
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
